import React, { useMemo, useState } from "react";

import { Modal } from "shared/components/Modal";
import { useVerifyIdFraudRiskCheck } from "shared/hooks/api";
import useModal from "shared/hooks/useModal";

import { SENTILINK_VERIFY_REVIEWED_ITEMS } from "./constants";
import { ShieldCheckIcon } from "./ShieldCheckIcon";

export { SENTILINK_VERIFY_REVIEWED_ITEMS } from "./constants";

const OTHER_REVIEWED_ITEM = "OTHER";

/** PM prototype Chevron (shared.jsx). */
const ChevronIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M4 6l4 4 4-4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export interface VerifyIdFraudRiskModalContext {
  applicationId: string | number;
  providerSentilinkId?: number | null;
  applicantName?: string;
  applicantRole?: string;
  riskLabel?: string;
  reasonCount?: number;
  checkedAt?: string | null;
  onSuccess?: () => void;
}

const formatCheckedAt = (checkedAt?: string | null) => {
  if (!checkedAt) {
    return null;
  }
  const parsed = new Date(checkedAt);
  if (Number.isNaN(parsed.getTime())) {
    return checkedAt;
  }
  return parsed.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/** Map API party slugs to PM Deal Summary labels (e.g. Primary applicant). */
export const formatApplicantRoleLabel = (
  role?: string | null
): string | undefined => {
  if (!role) {
    return undefined;
  }
  const normalized = role.trim().toLowerCase().replace(/_/g, "-");
  const labels: Record<string, string> = {
    "primary-applicant": "Primary applicant",
    primary: "Primary applicant",
    "co-applicant": "Co-applicant",
    coapplicant: "Co-applicant",
    applicant: "Applicant",
    guarantor: "Guarantor",
    occupant: "Occupant",
  };
  if (labels[normalized]) {
    return labels[normalized];
  }
  // Already human-readable (e.g. "Primary applicant") or unknown — title-case words
  if (role.includes(" ") || role.includes("-")) {
    return role
      .split(/[\s-]+/)
      .filter(Boolean)
      .map((w, i) =>
        i === 0
          ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
          : w.toLowerCase()
      )
      .join(" ");
  }
  return role.charAt(0).toUpperCase() + role.slice(1);
};

export const VerifyIdFraudRiskModal: React.FC = () => {
  const { currentModalContext, selfApi } = useModal();
  const {
    applicationId,
    providerSentilinkId,
    applicantName,
    applicantRole,
    riskLabel = "Needs Review",
    reasonCount = 0,
    checkedAt,
    onSuccess,
  } = (currentModalContext || {}) as VerifyIdFraudRiskModalContext;

  const [reviewedItem, setReviewedItem] = useState<string | undefined>(
    undefined
  );
  const [details, setDetails] = useState("");
  const [showErrors, setShowErrors] = useState(false);

  const { verifyIdFraudRiskCheck, isVerifyingIdFraudRiskCheck } =
    useVerifyIdFraudRiskCheck({
      onSuccess: () => {
        if (onSuccess) {
          onSuccess();
        }
        selfApi.closeDialog();
      },
    });

  const displayName = applicantName;
  const displayRole = formatApplicantRoleLabel(applicantRole);

  const detailsRequired = reviewedItem === OTHER_REVIEWED_ITEM;
  const reviewedItemError =
    showErrors && !reviewedItem ? "Select what you reviewed." : undefined;
  const detailsError =
    showErrors && detailsRequired && !details.trim()
      ? 'Details are required when "Other" is selected.'
      : undefined;

  const firstName = displayName?.split(" ")[0] || "the applicant";
  const checkedAtDisplay = formatCheckedAt(checkedAt);
  const canConfirm = Boolean(
    reviewedItem && !(detailsRequired && !details.trim())
  );

  const subtitle = useMemo(() => {
    if (!displayName) {
      return undefined;
    }
    return (
      <span className="reset-dialog-sub-inner">
        <span>{displayName}</span>
        {displayRole && (
          <>
            <span className="dot-sep" aria-hidden="true">
              {"\u00b7"}
            </span>
            <span className="reset-step-chip">{displayRole}</span>
          </>
        )}
      </span>
    );
  }, [displayName, displayRole]);

  const handleSubmit = () => {
    if (isVerifyingIdFraudRiskCheck) {
      return;
    }

    setShowErrors(true);
    if (!reviewedItem || (detailsRequired && !details.trim())) {
      return;
    }

    verifyIdFraudRiskCheck({
      applicationId,
      providerSentilinkId,
      reviewedItem,
      details: details.trim(),
      applicantFirstName: firstName !== "the applicant" ? firstName : undefined,
    });
  };

  return (
    <Modal
      showTitle={false}
      showFooter={false}
      closable={false}
      centered
      width={480}
    >
      {/* Strip shared Modal.Body 40/48px padding — dialog brings its own 22px. */}
      <Modal.Body noPaddingX>
        <div className="idfraudrisk-verify-dialog">
          <div className="reset-dialog-head">
            <span className="reset-dialog-icon verify">
              <ShieldCheckIcon size={18} />
            </span>
            <div className="reset-dialog-titles">
              <h3 className="reset-dialog-title">
                Verify Identity Risk Check
              </h3>
              {subtitle && <p className="reset-dialog-sub">{subtitle}</p>}
            </div>
            <button
              type="button"
              className="reset-dialog-close"
              onClick={() => selfApi.closeDialog()}
              aria-label="Close"
            >
              {"\u2715"}
            </button>
          </div>

          <div className="reset-dialog-body">
            <div className="verify-explainer">
              <p className="ve-lead">
                Verify this check only after you confirm {firstName}&apos;s
                identity yourself.
              </p>
              <ul className="ve-list">
                <li>Pick what you reviewed below.</li>
                <li>
                  This <b>clears the check</b> and <b>re-runs Lumen</b>.
                </li>
                <li>The original result stays in the activity log.</li>
              </ul>
            </div>

            <div className="verify-context">
              <span className="verify-context-eyebrow">
                Verifying this result
              </span>
              <div className="verify-context-row">
                <span
                  className={`tag ${
                    riskLabel === "High Risk" ? "blocked" : "warn"
                  }`}
                >
                  {riskLabel}
                </span>
                <span className="verify-context-meta">
                  {reasonCount} reason code{reasonCount === 1 ? "" : "s"}
                  {checkedAtDisplay ? ` \u00b7 checked ${checkedAtDisplay}` : ""}
                </span>
              </div>
            </div>

            <div className="reset-form">
              <label className="reset-field">
                <span className="reset-label">
                  What did you review? <span className="req">*</span>
                </span>
                <div
                  className={`reset-select-wrap ${
                    reviewedItemError ? "error" : ""
                  }`}
                >
                  <select
                    id="idfraudrisk-verify-reviewed-item"
                    name="reviewedItem"
                    className={`reset-select${!reviewedItem ? " is-placeholder" : ""}`}
                    value={reviewedItem || ""}
                    onChange={(event) => setReviewedItem(event.target.value)}
                  >
                    <option value="">Select what you reviewed...</option>
                    {SENTILINK_VERIFY_REVIEWED_ITEMS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <span className="reset-select-caret">
                    <ChevronIcon size={14} />
                  </span>
                </div>
                {reviewedItemError && (
                  <span className="reset-err">{reviewedItemError}</span>
                )}
              </label>

              <label className="reset-field">
                <span className="reset-label">
                  Details{" "}
                  <span className="reset-opt">
                    {detailsRequired ? "(required)" : "(optional)"}
                  </span>
                </span>
                <textarea
                  id="idfraudrisk-verify-details"
                  name="details"
                  className={`reset-textarea ${detailsError ? "error" : ""}`}
                  rows={3}
                  placeholder={
                    detailsRequired
                      ? "Required when Other is selected - describe what you reviewed for the audit log."
                      : "Add context for the audit log (e.g. document number, who you spoke with)..."
                  }
                  value={details}
                  maxLength={2048}
                  onChange={(event) => setDetails(event.target.value)}
                />
                {detailsError && (
                  <span className="reset-err">{detailsError}</span>
                )}
              </label>
            </div>
          </div>

          <div className="reset-footer">
            <button
              type="button"
              className="btn ghost"
              onClick={() => selfApi.closeDialog()}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="verify-idfraudrisk-submit"
              className={`btn verify-confirm ${canConfirm ? "" : "is-disabled"}`}
              onClick={handleSubmit}
              aria-disabled={!canConfirm}
              disabled={isVerifyingIdFraudRiskCheck}
            >
              <ShieldCheckIcon size={14} /> Verify
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default VerifyIdFraudRiskModal;
